import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { ScopedEntity } from '../../db/ScopedEntity.js';
import { CampusLesson } from './CampusLesson.js';

/**
 * Defines if a Section and alle of its child contents are for students or for employees
 */
export enum CampusArea {
  STUDENTS = 'Schüler:innen',
  EMPLOYEES = 'Mitarbeiter:innen',
  EXTERNAL_EMPLOYEES = 'Externe Mitarbeiter:innen',
}

@Entity()
export class CampusSection extends ScopedEntity {
  public static override _class = 'CampusSection';
  public static override _collectionName = 'campus-sections';

  static override get allFields() {
    return selectionSet(() => CampusSection, 2);
  }

  @validate(({ value, key, source }) => {
    if (!value) throw new ValidationError(`Bitte gebe einen Namen an!`, { key, source });
  })
  @Field(type => String)
  public name!: string;

  @Field(type => String, { default: v => v ?? CampusArea.STUDENTS })
  public area!: string;

  @Field(type => [Int], {
    default: v => v ?? [],
  })
  public forGrades!: number[];

  @Field(type => [String], {
    default: v => v ?? [],
  })
  public subjects!: string[];

  @Field(type => Boolean, {
    default: v => v ?? true,
  })
  public hiddenFromStudents!: boolean;

  @Field(type => [String], {
    default: v => v ?? [],
  })
  public lessonIDs: Maybe<string[]>;

  @Field(type => [CampusLesson], {
    resolve: function (this: CampusSection) {
      return async () => {
        if (!this.lessonIDs?.length) return [];

        // Resolve Lessons
        const lessons = await loadMongoData(CampusLesson, this.lessonIDs);
        // Sort lessons after array because getById is not sorted
        const newLessonOrder: Array<EntityData<CampusLesson>> = [];
        this.lessonIDs.forEach(lessonID => {
          const lesson = lessons.find(l => l._id === lessonID) as Maybe<EntityData<CampusLesson>>;
          if (!lesson) {
            console.warn('Something went wrong while sorting, this should not happen!');
            return;
          }
          newLessonOrder.push(lesson);
        });

        return newLessonOrder as CampusLesson[];
      };
    },
  })
  public lessons: Maybe<CampusLesson[]>;
}
